import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormViewer from "../components/UI/FormViewer";
import Loader from "../components/UI/Loader";
import { getClientFormInstance } from "../services/formsService";
import { deleteFileUpload, getClientRelatedFiles, getDownloadUrl } from "../services/fileService";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import { ClientRelatedFile } from "../models/serviceModels/ClientRelatedFile";
import { format } from "date-fns";
import IconButton from "../components/UI/IconButton";
import Dropdown from "../components/UI/Dropdown";
import Modal from "../components/UI/Modal";
import Button from "../components/UI/Button";
import { allParticipantFilesEnabled } from "../utils/features";

const ParticipantAdditionalInformation = () => {
    const { id = '' } = useParams();
    const [loading, setLoading] = useState(true);
    const [clientForm, setClientForm] = useState<any>(null);
    const [apiError, setApiError] = useState<any>();
    const [relatedFiles, setRelatedFiles] = useState<ClientRelatedFile[]>([]);
    const [filesExpanded, setFilesExpanded] = useState(false);
    const [openingFiles, setOpeningFiles] = useState<string[]>([]);
    const [fileToRemove, setFileToRemove] = useState<ClientRelatedFile>();
    const [deletingFile, setDeletingFile] = useState(false);
    const [modalApiError, setModalApiError] = useState<any>();

    useEffect(() => {
        Promise.all([getClientFormInstance(id), getClientRelatedFiles(id)]).then(([fi, rf]) => {
            setRelatedFiles(rf);
            setClientForm(fi);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const getFileSource = (referenceType: string, referenceDescription: string) => {
        switch(referenceType) {
            case 'case_plan': return `Case Plan (${referenceDescription})`;
            case 'client': return 'Profile';
            case 'client_note': return 'Note';
            case 'form_instance_field_file': return `Form Submission (${referenceDescription})`;
            default: return ''
        }
    };

    const handleOnOpen = (fileId: string) => {
        setOpeningFiles([
            ...openingFiles,
            fileId
        ]);
        
        getDownloadUrl({
            fileId
        }).then(r => {
            if (r.url) {
                const anchor = document.createElement('a');
                anchor.href = r.url;
                anchor.setAttribute('download', 'download');
                anchor.click();
            }
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setOpeningFiles([
                ...openingFiles.filter(o => o !== fileId)
            ]);
        });
    };

    const handleOnConfirmDeleteFile = (file: ClientRelatedFile) => {
        setFileToRemove(file);
        setDeletingFile(false);
        setModalApiError(undefined);
    };

    const handleOnCancelConfirmDelete = () => {
        setFileToRemove(undefined);
        setDeletingFile(false);
        setModalApiError(undefined);
    };

    const handleOnDeleteFile = () => {
        if (!fileToRemove) {
            console.warn('No file selected, unable to delete');
            return;
        }

        setDeletingFile(true);

        deleteFileUpload({
            fileId: fileToRemove.fileId
        }).then(() => {
            getClientRelatedFiles(id).then(rf => {
                setRelatedFiles(rf);
                handleOnCancelConfirmDelete();
            }).catch(e => {
                console.error(e);
                setModalApiError(e);
            }).finally(() => {
                setDeletingFile(false);
            })
        }).catch(e => {
            console.error(e);
            setModalApiError(e);
            setDeletingFile(false);
        });
    }

    if (loading) {
        return <Loader />
    }

    return (
        <>
            <ApiErrorMessages error={apiError} />
            {allParticipantFilesEnabled && <div className="row padding-2 margin-top-3 border-bottom">
                <div className="column">
                    <div className="row align-items-center">
                        <div className="column-auto">
                            <IconButton className="small flat"
                                id="expand-collapse-profile"
                                icon={filesExpanded ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                                onClick={() => setFilesExpanded(!filesExpanded)} />
                        </div>
                        <div className="column-auto">
                            <h2>All Participant Files ({relatedFiles.length})</h2>
                        </div>
                        <div className="column-auto">
                            {openingFiles.length !== 0 && <Loader />}
                        </div>
                    </div>
                    {filesExpanded && <div className="row margin-bottom-3">
                        <div className="column">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>file name</th>
                                        <th>source</th>
                                        <th>uploaded</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {relatedFiles.length === 0 && <tr>
                                        <td colSpan={4}>
                                            <div className="row justify-content-center">
                                                <div className="column-auto">
                                                    No files found
                                                </div>
                                            </div>
                                        </td>
                                    </tr>}
                                    {relatedFiles.length !== 0 && relatedFiles.map(rf => {
                                        const options: {label: string; href?: string; onClick?: () => void; newWindow?: boolean;}[] = [
                                            {
                                                label: 'Open File',
                                                onClick: () => handleOnOpen(rf.fileId)
                                            }
                                        ];

                                        if (rf.referenceType === 'case_plan') {
                                            options.push({
                                                label: 'Open Case Plan',
                                                href: `/participants/${id}/plans/${rf.primaryReferenceId}`,
                                                newWindow: true
                                            });
                                        } else if (rf.referenceType === 'client_note') {
                                            options.push({
                                                label: 'Open Note',
                                                href: `/participants/${id}/notes/${rf.primaryReferenceId}`,
                                                newWindow: true
                                            })
                                        } else if (rf.referenceType === 'form_instance_field_file') {
                                            options.push({
                                                label: 'Open Form Submission',
                                                href: `/forms/${rf.primaryReferenceId}/versions/${rf.secondaryReferenceId}/instances/${rf.teriaryReferenceId}/view`,
                                                newWindow: true
                                            })
                                        }

                                        options.push({
                                            label: 'Delete File',
                                            onClick: () => handleOnConfirmDeleteFile(rf)
                                        });

                                        return <tr>
                                            <td>{rf.fileName}{rf.fileExtension}</td>
                                            <td>{getFileSource(rf.referenceType, rf.referenceDescription)}</td>
                                            <td>{format(new Date(rf.fileCreated), 'Pp')}</td>
                                            <td>
                                                <Dropdown disabled={openingFiles.length !== 0} 
                                                    id={`participant-files-options-${rf.fileId}`} 
                                                    options={options} 
                                                    optionSize="large" />
                                            </td>
                                        </tr>
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>}
                </div>
            </div>}
            {clientForm && (<div>
                <div className="row">
                    <div className="column-auto">
                        <h2>Additional Information</h2>
                    </div>
                </div>
                <FormViewer instance={clientForm} />
            </div>)}
            <Modal id="file-upload-delete-modal" visible={fileToRemove !== undefined} className="small" title="Confirm File Remove" onClose={handleOnCancelConfirmDelete}>
                <>
                    <ApiErrorMessages error={modalApiError} />
                    <div className="row">
                        <div className="column">
                            <p>Are you sure you want to remove this file?</p>
                            <p>{fileToRemove?.fileName}</p>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-remove" 
                                        text="No" 
                                        className="secondary" 
                                        disabled={deletingFile}
                                        onClick={handleOnCancelConfirmDelete} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-remove" 
                                        text="Yes" 
                                        className="warning" 
                                        disabled={deletingFile}
                                        loading={deletingFile}
                                        onClick={handleOnDeleteFile} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
        </>
    );
};

export default ParticipantAdditionalInformation;
